/** The hub's keys (spec §7), handled in a capture listener on the hub so nothing behind it sees them. */
import { useEffect, useRef, type RefObject } from "react";

export interface HubKeyboardOptions {
  hubRef: RefObject<HTMLElement | null>;
  fieldRef: RefObject<HTMLInputElement | null>;
  query: string;
  onClearQuery: () => void;
  onTypeCharacter?: (character: string) => void;
  onStepChip: (step: 1 | -1) => void;
  onMoveSelection: (step: 1 | -1) => void;
  onOpenSelected: () => void;
  onClose: () => void;
}

function isTextEntry(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable)
  );
}

/** A character key; Space stays with a focused button. */
function isTypedCharacter(event: KeyboardEvent): boolean {
  return (
    event.key.length === 1 &&
    event.key !== " " &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey
  );
}

export function useHubKeyboard(options: HubKeyboardOptions): void {
  const latest = useRef(options);
  latest.current = options;
  const { hubRef } = options;

  useEffect(() => {
    const hub = hubRef.current;
    if (!hub) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return;
      const current = latest.current;
      const inField = event.target === current.fieldRef.current;
      if (event.key === "Escape") {
        // Menus and the rename dialog handle their own Esc first; either way the page never sees it.
        event.stopPropagation();
        if (event.defaultPrevented) return;
        event.preventDefault();
        if (current.query) current.onClearQuery();
        else current.onClose();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (!inField && isTextEntry(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        current.onMoveSelection(event.key === "ArrowDown" ? 1 : -1);
        return;
      }
      if (
        (event.key === "ArrowLeft" || event.key === "ArrowRight") &&
        inField &&
        !current.query
      ) {
        event.preventDefault();
        event.stopPropagation();
        current.onStepChip(event.key === "ArrowRight" ? 1 : -1);
        return;
      }
      if (event.key === "Enter" && inField) {
        event.preventDefault();
        event.stopPropagation();
        current.onOpenSelected();
        return;
      }
      if (isTypedCharacter(event) && !isTextEntry(event.target)) {
        // Focus moves before the character is typed, so it lands in the field, not the input bar.
        event.stopPropagation();
        current.fieldRef.current?.focus();
        if (current.onTypeCharacter) {
          event.preventDefault();
          current.onTypeCharacter(event.key);
        }
      }
    };
    hub.addEventListener("keydown", onKeyDown, true);
    return () => hub.removeEventListener("keydown", onKeyDown, true);
  }, [hubRef]);
}
